import React from "react";
import {
    Grid,
    Box,
    Stack,
    Chip,
    Typography,
    useTheme,
    Paper,
    alpha,
} from "@mui/material";

import DateRangePicker from "../../../inputs/DateRangePicker/DateRangePicker";

import {
    Event as EventIcon,
    CalendarMonth as CalendarIcon,
    Nightlight as NightIcon,
} from "@mui/icons-material";

const DateRangeSection = ({
    formData,
    errors,
    isArabic,
    handleDateChange,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    return (
        <Grid container spacing={3}>
            {/* Date Picker */}
            <Grid item xs={12}>
                <DateRangePicker
                    startDate={
                        formData.start_date || ""
                    }
                    endDate={
                        formData.end_date || ""
                    }
                    onStartDateChange={(val) =>
                        handleDateChange(
                            "start_date",
                            val
                        )
                    }
                    onEndDateChange={(val) =>
                        handleDateChange(
                            "end_date",
                            val
                        )
                    }
                    error={
                        !!errors.start_date ||
                        !!errors.end_date
                    }
                    helperText={
                        errors.start_date ||
                        errors.end_date
                    }
                    required
                    isArabic={isArabic}
                    monthsToShow={2} // 🔥 أهم تعديل: شهرين بدون أسهم
                />
            </Grid>

            {/* Summary Card */}
            {formData.start_date &&
                formData.end_date && (
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: isDark
                                    ? `linear-gradient(135deg, ${alpha(
                                        theme.palette
                                            .primary.main,
                                        0.15
                                    )} 0%, ${alpha(
                                        theme.palette
                                            .secondary
                                            .main,
                                        0.15
                                    )} 100%)`
                                    : `linear-gradient(135deg, ${alpha(
                                        theme.palette
                                            .primary.main,
                                        0.08
                                    )} 0%, ${alpha(
                                        theme.palette
                                            .secondary
                                            .main,
                                        0.08
                                    )} 100%)`,
                                border: `2px solid ${alpha(
                                    theme.palette.primary
                                        .main,
                                    isDark ? 0.3 : 0.2
                                )}`,
                                position: "relative",
                                overflow: "hidden",
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: 4,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                },
                            }}
                        >
                            <Grid
                                container
                                spacing={3}
                                alignItems="center"
                            >
                                {/* Icon */}
                                <Grid item xs={12} sm="auto">
                                    <Box
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            borderRadius:
                                                "50%",
                                            background:
                                                "linear-gradient(135deg, #1976d2, #9c27b0)",
                                            display: "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            boxShadow: `0 4px 20px ${alpha(
                                                theme
                                                    .palette
                                                    .primary
                                                    .main,
                                                0.4
                                            )}`,
                                        }}
                                    >
                                        <EventIcon
                                            sx={{
                                                fontSize: 36,
                                                color: "#fff",
                                            }}
                                        />
                                    </Box>
                                </Grid>

                                {/* Duration */}
                                <Grid item xs={12} sm>
                                    <Typography
                                        variant="overline"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ mb: 1 }}
                                    >
                                        {isArabic
                                            ? "مدة البرنامج"
                                            : "PROGRAM DURATION"}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        flexWrap="wrap"
                                    >
                                        <Chip
                                            icon={
                                                <CalendarIcon />
                                            }
                                            label={`${formData.durations_days} ${isArabic
                                                    ? "يوم"
                                                    : formData.durations_days ===
                                                        1
                                                        ? "Day"
                                                        : "Days"
                                                }`}
                                            color="primary"
                                            sx={{
                                                fontWeight: 700,
                                            }}
                                        />

                                        {(formData.durations_nights ||
                                            0) > 0 && (
                                                <Chip
                                                    icon={
                                                        <NightIcon />
                                                    }
                                                    label={`${formData.durations_nights} ${isArabic
                                                            ? formData.durations_nights ===
                                                                1
                                                                ? "ليلة"
                                                                : "ليالي"
                                                            : formData.durations_nights ===
                                                                1
                                                                ? "Night"
                                                                : "Nights"
                                                        }`}
                                                    color="secondary"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            )}
                                    </Stack>
                                </Grid>

                                {/* Dates */}
                                <Grid item xs={12} sm="auto">
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor:
                                                isDark
                                                    ? alpha(
                                                        "#fff",
                                                        0.05
                                                    )
                                                    : alpha(
                                                        "#000",
                                                        0.03
                                                    ),
                                            minWidth: 150,
                                        }}
                                    >
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {isArabic
                                                        ? "من"
                                                        : "FROM"}
                                                </Typography>
                                                <Typography
                                                    fontWeight={700}
                                                >
                                                    {
                                                        formData.start_date
                                                    }
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    height: 1,
                                                    bgcolor:
                                                        "divider",
                                                }}
                                            />

                                            <Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {isArabic
                                                        ? "إلى"
                                                        : "TO"}
                                                </Typography>
                                                <Typography
                                                    fontWeight={700}
                                                >
                                                    {
                                                        formData.end_date
                                                    }
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                )}
        </Grid>
    );
};

export default DateRangeSection;